import { useMemo, useState } from "react";
import {
  RhythmTypicalLookback,
  useTimeTrackerStore,
} from "../store/timeTrackerStore";
import {
  closeGaps,
  DAY_COLUMN_GAP_MS,
  DAY_CUTOFF_HOUR,
  DayInterval,
  entriesByLocalDay,
  fitHourRange,
  formatDurationCompact,
  formatTimeOfDay,
  fractionOfDaysActive,
  logicalDayStart,
  pickRecentDays,
  summarizeDay,
  totalWorkMs,
  TYPICAL_BUCKET_MS,
  TYPICAL_INTERNAL_GAP_MS,
  TYPICAL_NOISE_FLOOR,
} from "../utils/rhythm";
import { median } from "../utils/median";
import { Button, Card } from "@lazar/ui";

// How many past working days to show alongside Today as visual columns.
const PAST_DAY_COUNT = 6;
// Minimum total work for a day to qualify as a column / typical-pool member.
// Lenient — a quick 5-minute session still tells you "you were online at
// that time." Days below this are skipped, with the next working day sliding
// in to fill the slot.
const PAST_DAY_MIN_WORK_MS = 5 * 60 * 1000;
// User-facing labels for the typical-day lookback chips. The numeric values
// are the actual lookback (and must stay in sync with the
// RhythmTypicalLookback union in the store).
const LOOKBACK_OPTIONS: readonly RhythmTypicalLookback[] = [
  5, 10, 20, 40, 60, 180, 260,
];
// Short chip label per preset. 260 working days ≈ a working year, so it reads
// "1y" rather than a noisy "260d".
const lookbackChipLabel = (option: RhythmTypicalLookback): string =>
  option === 260 ? "1y" : `${option}d`;
const HOUR_MS = 60 * 60 * 1000;

// Local-day label shaped like "Wed 27". Today gets "Today". Used as the column
// header — short on purpose because columns get narrow.
const formatDayLabel = (dayStartMs: number, todayStartMs: number): string => {
  if (dayStartMs === todayStartMs) return "Today";
  const d = new Date(dayStartMs);
  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
  return `${dow} ${d.getDate()}`;
};

// One filled block + the break-gap label below it. Positioning is absolute
// within the parent column so hour gridlines and blocks share a coordinate
// system. The interval's start/end are ms-since-midnight, the column's
// fromMs/toMs are the visible window's bounds.
interface ColumnBlocksProps {
  intervals: DayInterval[];
  fromMs: number;
  toMs: number;
}

const ColumnBlocks = ({ intervals, fromMs, toMs }: ColumnBlocksProps) => {
  const totalMs = toMs - fromMs;
  if (totalMs <= 0) return null;

  return (
    <>
      {intervals.map((iv, idx) => {
        const s = Math.max(iv.startMs, fromMs);
        const e = Math.min(iv.endMs, toMs);
        if (e <= s) return null;
        const topPct = ((s - fromMs) / totalMs) * 100;
        const heightPct = ((e - s) / totalMs) * 100;
        const labelInside = e - s >= 25 * 60 * 1000; // ≥25min → fits the times
        const nextStart =
          idx + 1 < intervals.length ? intervals[idx + 1].startMs : null;
        const gap = nextStart !== null ? nextStart - iv.endMs : 0;
        const showGapLabel = nextStart !== null && gap >= 10 * 60 * 1000;
        const gapTopPct = ((iv.endMs - fromMs) / totalMs) * 100;
        const gapHeightPct = (gap / totalMs) * 100;
        return (
          <div
            key={idx}
            className="absolute left-1 right-1"
            style={{ top: `${topPct}%`, height: `${heightPct}%` }}
          >
            <div className="h-full w-full bg-sky-brut border-2 border-ink dark:border-cream rounded-sm overflow-hidden">
              {labelInside && (
                <div className="px-1 py-0.5 text-[10px] font-bold text-ink leading-tight">
                  {formatTimeOfDay(iv.startMs)}
                  <br />
                  {formatTimeOfDay(iv.endMs)}
                </div>
              )}
            </div>
            {showGapLabel && (
              <div
                className="absolute left-0 right-0 flex items-center justify-center text-[9px] font-bold text-ink-soft dark:text-cream/60 pointer-events-none"
                style={{
                  top: `${((gapTopPct - topPct) / heightPct) * 100}%`,
                  height: `${(gapHeightPct / heightPct) * 100}%`,
                }}
              >
                {formatDurationCompact(gap)}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

// Typical-column rendering. Each bucket is a thin horizontal slice whose fill
// opacity tracks the fraction-of-days-active for that bucket. Buckets below
// the noise floor render blank — keeps the column readable on scattered data
// instead of foggy.
interface TypicalColumnProps {
  fractions: number[];
  bucketMs: number;
  fromMs: number;
  toMs: number;
}

const TypicalColumn = ({
  fractions,
  bucketMs,
  fromMs,
  toMs,
}: TypicalColumnProps) => {
  const totalMs = toMs - fromMs;
  if (totalMs <= 0) return null;
  return (
    <>
      {fractions.map((frac, i) => {
        if (frac < TYPICAL_NOISE_FLOOR) return null;
        const top = ((i * bucketMs) / totalMs) * 100;
        const height = (bucketMs / totalMs) * 100;
        // Map [noiseFloor, 1] → [0.25, 1] for a perceptible floor without a
        // hard edge — a 25%-of-days bucket is still visibly there.
        const opacity =
          0.25 + ((frac - TYPICAL_NOISE_FLOOR) / (1 - TYPICAL_NOISE_FLOOR)) * 0.75;
        return (
          <div
            key={i}
            className="absolute left-1 right-1 bg-sky-brut"
            style={{
              top: `${top}%`,
              height: `${height}%`,
              opacity,
            }}
            aria-hidden="true"
          />
        );
      })}
    </>
  );
};

// One vertical column: fixed-height header + a flex-grown strip. The strip is
// flex-1 with min-h-0 so columns stretch to fill the parent strip area's
// height — the panel's outer flex layout pushes every available pixel into
// the strips. Used for both real day columns (children = ColumnBlocks) and
// the typical column (children = TypicalColumn).
interface ColumnProps {
  label: string;
  sublabel?: string;
  emphasized?: boolean;
  fromMs: number;
  toMs: number;
  hourGridHours: number[];
  children: React.ReactNode;
}

const Column = ({
  label,
  sublabel,
  emphasized,
  fromMs,
  toMs,
  hourGridHours,
  children,
}: ColumnProps) => {
  const totalMs = toMs - fromMs;
  return (
    <div className="flex flex-col items-stretch min-w-[72px] flex-1 min-h-0">
      <div className="h-10 flex flex-col items-center justify-end pb-1 shrink-0">
        <div
          className={`text-center text-xs font-black leading-tight ${
            emphasized
              ? "text-ink dark:text-cream"
              : "text-ink-soft dark:text-cream/70"
          }`}
        >
          {label}
        </div>
        {sublabel !== undefined && (
          <div className="text-center text-[10px] font-mono text-ink-soft dark:text-cream/60 leading-tight">
            {sublabel}
          </div>
        )}
      </div>
      <div
        className={`relative flex-1 min-h-0 border-2 overflow-hidden ${
          emphasized
            ? "border-ink dark:border-cream"
            : "border-ink/40 dark:border-cream/40"
        } bg-lemon-50 dark:bg-charcoal rounded-sm`}
      >
        {hourGridHours.map((h) => {
          const topMs = h * HOUR_MS - fromMs;
          const topPct = (topMs / totalMs) * 100;
          if (topPct <= 0 || topPct >= 100) return null;
          return (
            <div
              key={h}
              className="absolute left-0 right-0 border-t border-ink/15 dark:border-cream/15"
              style={{ top: `${topPct}%` }}
              aria-hidden="true"
            />
          );
        })}
        {children}
      </div>
    </div>
  );
};

const WorkRhythmPanel = () => {
  // Re-capture `now` only when this body mounts (the modal mounts AnalyticsPanels
  // body per-open, so "today" stays the day the user opened the modal). The
  // hooks below depend on `now` only as a reference for splitting/clamping. A
  // lazy useState initializer (not useMemo) keeps Date.now() out of render —
  // it runs exactly once at mount and the value is stable thereafter, which is
  // the mount-anchor semantics we want and what react-hooks/purity requires.
  const [now] = useState(() => Date.now());
  const { timeEntries, settings, setRhythmTypicalLookback } =
    useTimeTrackerStore();
  const allEntries = useMemo(() => Object.values(timeEntries), [timeEntries]);
  const lookback = settings.rhythmTypicalLookback;

  const todayStart = useMemo(() => logicalDayStart(now), [now]);

  // Step 1: split every entry into per-logical-day intervals (raw).
  const byDayRaw = useMemo(() => entriesByLocalDay(allEntries, now), [
    allEntries,
    now,
  ]);

  // Step 2a: VISUAL smoothing for the day columns — hardcoded 15-min gap
  // close. Drives only the painted day-column shapes. Reading any number
  // off this map (totals, breaks, first/last) would mix visual smoothing
  // into the summary cards, which is exactly what we want to avoid. Use
  // byDayForSummary below for any user-facing number instead.
  const byDayDisplay = useMemo(() => {
    const out = new Map<number, DayInterval[]>();
    for (const [day, ivs] of byDayRaw) {
      out.set(day, closeGaps(ivs, DAY_COLUMN_GAP_MS));
    }
    return out;
  }, [byDayRaw]);

  // Step 2b: REPORTING smoothing — uses the user's global settings.smoothing
  // configuration (gated on `enabled`, with `thresholdMinutes`). Drives the
  // numbers in the Today/Typical summary cards AND the qualifying-day
  // filter for the typical aggregation, so what the user sees in the cards
  // matches billing/analytics elsewhere.
  const reportingSmoothingMs = settings.smoothing.enabled
    ? settings.smoothing.thresholdMinutes * 60_000
    : 0;
  const byDayForSummary = useMemo(() => {
    if (reportingSmoothingMs <= 0) return byDayRaw;
    const out = new Map<number, DayInterval[]>();
    for (const [day, ivs] of byDayRaw) {
      out.set(day, closeGaps(ivs, reportingSmoothingMs));
    }
    return out;
  }, [byDayRaw, reportingSmoothingMs]);

  // Today's intervals — visual (day columns) vs reporting (Today card)
  // versions kept separate. The `?? []` fallback identities are memoised so
  // the hour-range useMemo below doesn't rebuild every render.
  const todayIntervals = useMemo(
    () => byDayDisplay.get(todayStart) ?? [],
    [byDayDisplay, todayStart],
  );
  const todayIntervalsForSummary = useMemo(
    () => byDayForSummary.get(todayStart) ?? [],
    [byDayForSummary, todayStart],
  );

  // Pick the most recent 6 past days with at least PAST_DAY_MIN_WORK_MS work,
  // sliding back as needed (zero-work days are skipped). The start cursor is
  // yesterday's logical-day start (DST-safe via pickRecentDays' walk-back).
  const pastDays = useMemo(
    () =>
      pickRecentDays(
        byDayDisplay,
        logicalDayStart(todayStart - 1),
        PAST_DAY_COUNT,
        (ivs) => totalWorkMs(ivs) >= PAST_DAY_MIN_WORK_MS,
      ),
    [byDayDisplay, todayStart],
  );

  // Typical pool: the most recent `lookback` working days (any work above
  // the noise floor), measured against the reporting smoothing so the
  // sample matches the lens the Typical card uses for its numbers. This is
  // INDEPENDENT of the 6 visual day columns above — the calendar week
  // stays 6 wide regardless of how wide the typical sample is.
  const typicalCandidates = useMemo(
    () =>
      pickRecentDays(
        byDayForSummary,
        logicalDayStart(todayStart - 1),
        lookback,
        (ivs) => totalWorkMs(ivs) >= PAST_DAY_MIN_WORK_MS,
      ),
    [byDayForSummary, todayStart, lookback],
  );

  // Qualifying subset of the typical pool — ≥ 0.25 × dayHours of
  // reporting-smoothed work. ONE filter here, two views downstream: the
  // column visual and the card numbers operate over the SAME day set, so
  // the "(N of M)" badge agrees with both.
  const dayHoursMs = settings.dayHours * HOUR_MS;
  const qualifyingPastDays = useMemo(() => {
    const out: number[] = [];
    for (const dayStart of typicalCandidates) {
      const ivs = byDayForSummary.get(dayStart) ?? [];
      if (totalWorkMs(ivs) >= 0.25 * dayHoursMs) out.push(dayStart);
    }
    return out;
  }, [typicalCandidates, byDayForSummary, dayHoursMs]);

  // Typical column visual: each qualifying past day re-smoothed at the
  // hardcoded 30-min gap so the bucket-shading reflects the recurring
  // pattern, not raw fragmentation. Drives only the painted shape.
  const typicalColumnDays = useMemo(() => {
    const out: DayInterval[][] = [];
    for (const dayStart of qualifyingPastDays) {
      const raw = byDayRaw.get(dayStart) ?? [];
      out.push(closeGaps(raw, TYPICAL_INTERNAL_GAP_MS));
    }
    return out;
  }, [qualifyingPastDays, byDayRaw]);

  // Typical card stats: per-day reporting-smoothed intervals for the same
  // qualifying day set. Drives only the headline numbers.
  const typicalReportingDays = useMemo(() => {
    const out: DayInterval[][] = [];
    for (const dayStart of qualifyingPastDays) {
      out.push(byDayForSummary.get(dayStart) ?? []);
    }
    return out;
  }, [qualifyingPastDays, byDayForSummary]);

  // Auto-fit hour range across today + all visible past days + the typical
  // column contributors. Pads to a 6-hour minimum span so a fresh user who
  // only worked 09:00–09:30 doesn't see a vertically squashed column.
  const [fromHour, toHour] = useMemo(() => {
    const visible = [todayIntervals, ...typicalColumnDays];
    for (const day of pastDays) {
      const ivs = byDayDisplay.get(day);
      if (ivs) visible.push(ivs);
    }
    return fitHourRange(visible, [5, 17], 6);
  }, [todayIntervals, pastDays, byDayDisplay, typicalColumnDays]);

  const fromMs = fromHour * HOUR_MS;
  const toMs = toHour * HOUR_MS;
  const totalMs = toMs - fromMs;

  // Hour gridline positions — every integer hour between from and to,
  // exclusive of the endpoints (those are the column's borders).
  const hourGridHours = useMemo(() => {
    const out: number[] = [];
    for (let h = fromHour + 1; h < toHour; h++) out.push(h);
    return out;
  }, [fromHour, toHour]);

  // Today card numbers — driven by the reporting smoothing, NOT the visual
  // day-column smoothing. So a 14-min gap counts as a break here if (and
  // only if) the user's global smoothing threshold is < 14 min.
  const todaySummary = summarizeDay(todayIntervalsForSummary);

  // Typical card numbers — each headline scalar is the median of the
  // per-day value across the reporting-smoothed qualifying set. Median (not
  // mean) so a single marathon day doesn't pull the typical start an hour
  // earlier than reality. Critically, insideBreakMs is the median of
  // per-day inside-breaks, not (median lastEnd − median firstStart − median
  // totalWork) — that mixed medians from different days and could surface
  // a "typical break" no day actually had.
  const typicalSummary = useMemo(() => {
    if (typicalReportingDays.length === 0) {
      return summarizeDay([]);
    }
    const firsts: number[] = [];
    const lasts: number[] = [];
    const totals: number[] = [];
    const insideBreaks: number[] = [];
    for (const day of typicalReportingDays) {
      const s = summarizeDay(day);
      if (s.firstStartMs !== null) firsts.push(s.firstStartMs);
      if (s.lastEndMs !== null) lasts.push(s.lastEndMs);
      totals.push(s.totalWorkMs);
      insideBreaks.push(s.insideBreakMs);
    }
    return {
      firstStartMs: firsts.length ? median(firsts) : null,
      lastEndMs: lasts.length ? median(lasts) : null,
      totalWorkMs: median(totals),
      insideBreakMs: median(insideBreaks),
      sessionCount: 0, // not surfaced — the fraction column shows the shape
    };
  }, [typicalReportingDays]);

  // Typical column shading — uses the visual-smoothed days. Independent
  // from the card numbers above (which use reporting smoothing).
  const typicalFractions = useMemo(
    () =>
      fractionOfDaysActive(
        typicalColumnDays,
        fromMs,
        toMs,
        TYPICAL_BUCKET_MS,
      ),
    [typicalColumnDays, fromMs, toMs],
  );

  // Hour-axis labels — one per integer hour in the visible range, positioned
  // by percentage of the strip's height so the labels track the strip's
  // dynamic flex-grown height without recomputing on resize.
  const axisLabels = useMemo(() => {
    const out: Array<{ h: number; topPct: number }> = [];
    for (let h = fromHour; h <= toHour; h++) {
      const topMs = h * HOUR_MS - fromMs;
      out.push({ h, topPct: (topMs / totalMs) * 100 });
    }
    return out;
  }, [fromHour, toHour, fromMs, totalMs]);

  if (allEntries.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-ink-soft dark:text-cream/70">
          Start tracking time to see your rhythm.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-6 flex flex-col h-full min-h-0">
      <h2 className="text-lg font-black dark:text-cream mb-1 shrink-0">
        When did I work?
      </h2>
      <p className="text-xs text-ink-soft dark:text-cream/60 mb-4 shrink-0">
        The calendar uses fixed visual smoothing — 15 min for day columns,
        30 min for the Typical column — so a quick interruption doesn't
        shatter the shape and the recurring pattern across days reads
        cleanly. The Today and Typical summary cards use your global
        smoothing setting from Settings, so their numbers line up with
        billing. A "day" here runs 04:00 → 04:00 next day, so late-night
        work stays with the day it started instead of ghost-starting the
        next morning at 00:00.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 shrink-0">
        <Card sub className="p-3">
          <div className="text-[10px] font-black uppercase tracking-wider text-ink-soft dark:text-cream/60 mb-1">
            Today
          </div>
          {todaySummary.firstStartMs === null ? (
            <div className="text-sm font-bold text-ink-soft dark:text-cream/70">
              No work tracked yet today.
            </div>
          ) : (
            <div className="space-y-0.5 text-sm">
              <div className="font-black text-ink dark:text-cream">
                Started at {formatTimeOfDay(todaySummary.firstStartMs)}
              </div>
              <div className="font-mono text-ink-soft dark:text-cream/70 text-xs">
                last work {formatTimeOfDay(todaySummary.lastEndMs ?? 0)} ·{" "}
                {formatDurationCompact(todaySummary.totalWorkMs)} worked ·{" "}
                {formatDurationCompact(todaySummary.insideBreakMs)} on break ·{" "}
                {todaySummary.sessionCount} session
                {todaySummary.sessionCount === 1 ? "" : "s"}
              </div>
            </div>
          )}
        </Card>
        <Card sub className="p-3">
          <div className="flex items-baseline justify-between gap-2 mb-1 flex-wrap">
            <div className="text-[10px] font-black uppercase tracking-wider text-ink-soft dark:text-cream/60">
              Typical day{" "}
              <span className="font-mono normal-case font-bold">
                ({qualifyingPastDays.length} of last{" "}
                {typicalCandidates.length} working days)
              </span>
            </div>
            <div
              role="group"
              aria-label="Typical-day lookback window"
              className="flex items-center gap-1"
            >
              {LOOKBACK_OPTIONS.map((option) => {
                const active = option === lookback;
                return (
                  <Button
                    key={option}
                    type="button"
                    onClick={() => setRhythmTypicalLookback(option)}
                    aria-pressed={active}
                    aria-label={`Typical lookback: last ${option} working days`}
                    variant={active ? "primary" : "default"}
                    className="px-2 py-0.5 text-[10px]"
                  >
                    {lookbackChipLabel(option)}
                  </Button>
                );
              })}
            </div>
          </div>
          {typicalSummary.firstStartMs === null ? (
            <div className="text-sm font-bold text-ink-soft dark:text-cream/70">
              Not enough working days yet to call out a typical pattern.
            </div>
          ) : (
            <div className="space-y-0.5 text-sm">
              <div className="font-black text-ink dark:text-cream">
                Starts around {formatTimeOfDay(typicalSummary.firstStartMs)}
              </div>
              <div className="font-mono text-ink-soft dark:text-cream/70 text-xs">
                wraps around {formatTimeOfDay(typicalSummary.lastEndMs ?? 0)} ·{" "}
                {formatDurationCompact(typicalSummary.totalWorkMs)} worked ·{" "}
                {formatDurationCompact(typicalSummary.insideBreakMs)} on break
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Strip area: flex-1 min-h-0 so it consumes every remaining vertical
          pixel of the pane. overflow-x-auto allows narrow viewports to scroll
          horizontally rather than crushing the columns. */}
      <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden">
        <div className="flex items-stretch gap-2 min-w-fit h-full">
          {/* Hour axis. Mirrors the column shape (h-10 header spacer + flex-1
              strip area) so labels line up exactly with the column gridlines
              regardless of strip height. */}
          <div className="flex flex-col shrink-0 w-8 min-h-0">
            <div className="h-10 shrink-0" aria-hidden="true" />
            <div className="relative flex-1 min-h-0" aria-hidden="true">
              {axisLabels.map(({ h, topPct }) => (
                <div
                  key={h}
                  className="absolute right-1 text-[10px] font-mono text-ink-soft dark:text-cream/60 -translate-y-1/2"
                  style={{ top: `${topPct}%` }}
                >
                  {((DAY_CUTOFF_HOUR + h) % 24).toString().padStart(2, "0")}
                </div>
              ))}
            </div>
          </div>

          {/* Today */}
          <Column
            label="Today"
            sublabel={
              todaySummary.totalWorkMs > 0
                ? formatDurationCompact(todaySummary.totalWorkMs)
                : "—"
            }
            emphasized
            fromMs={fromMs}
            toMs={toMs}
            hourGridHours={hourGridHours}
          >
            <ColumnBlocks
              intervals={todayIntervals}
              fromMs={fromMs}
              toMs={toMs}
            />
          </Column>

          {/* Past working days */}
          {pastDays.map((dayStartMs) => {
            const ivs = byDayDisplay.get(dayStartMs) ?? [];
            const s = summarizeDay(ivs);
            return (
              <Column
                key={dayStartMs}
                label={formatDayLabel(dayStartMs, todayStart)}
                sublabel={
                  s.totalWorkMs > 0 ? formatDurationCompact(s.totalWorkMs) : "—"
                }
                fromMs={fromMs}
                toMs={toMs}
                hourGridHours={hourGridHours}
              >
                <ColumnBlocks intervals={ivs} fromMs={fromMs} toMs={toMs} />
              </Column>
            );
          })}

          {/* Typical column — only renders if at least one day qualified. */}
          {qualifyingPastDays.length > 0 && (
            <>
              <div
                className="border-l-2 border-dashed border-ink/40 dark:border-cream/40 mx-1"
                aria-hidden="true"
              />
              <Column
                label="Typical"
                sublabel={
                  typicalSummary.totalWorkMs > 0
                    ? formatDurationCompact(typicalSummary.totalWorkMs)
                    : "—"
                }
                fromMs={fromMs}
                toMs={toMs}
                hourGridHours={hourGridHours}
              >
                <TypicalColumn
                  fractions={typicalFractions}
                  bucketMs={TYPICAL_BUCKET_MS}
                  fromMs={fromMs}
                  toMs={toMs}
                />
              </Column>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default WorkRhythmPanel;
